import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import AddCategoryDialog from '../../components/appointments/addCategoryDialog';
import { useNotifications } from '../../components/appointments/notifications';
const CategoryPage = ({ accountFromUrl, acctId }) => {
  // Initialize notifications system
  const { showSuccess, showError } = useNotifications();

  const [categories, setCategories] = useState([]);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // acctId is now received as a prop
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [linkedCalendars, setLinkedCalendars] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch categories when acctId changes
  useEffect(() => {
    if (acctId) {
      fetchCategories(acctId);
    } else {
      setCategories([]);
      showError('Account ID not found for this account.');
    }
    // eslint-disable-next-line
  }, [acctId]);

  const fetchCategories = async (targetAcctId = null) => {
    setIsLoading(true);
    try {
      const backendAcctId = acctId;
      if (!backendAcctId) {
        setCategories([]);
        showError('Account ID not found for this account.');
        setIsLoading(false);
        return;
      }
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ui/appointment/gCal/getCategories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ acctId: backendAcctId }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const responseData = await response.json();

      if (responseData.success && Array.isArray(responseData.data)) {
        setCategories(responseData.data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
      showError('Failed to load categories. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (category) => {
    setCurrentCategory(category);
    setEditMode(true);
    setOpenCategoryDialog(true);
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (categoryToDelete) {
      setIsDeleting(true);
      await deleteCategory(categoryToDelete._id);
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setIsDeleteConfirmOpen(false);
    setCategoryToDelete(null);
    setDeleteError(null);
    setLinkedCalendars([]);
  };

  const deleteCategory = async (categoryId) => {
    setIsLoading(true);
    setDeleteError(null);
    setLinkedCalendars([]);
    try {
      const backendAcctId = acctId;
      if (!backendAcctId) {
        showError('No account ID available for category deletion');
        setIsLoading(false);
        return;
      }
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/ui/appointment/gCal/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ acctId: backendAcctId }),
      });

      const data = await response.json();

      if (data && !data.success) {
        setDeleteError("Cannot delete because this category is linked with one or more calendars.");
      } else {
        fetchCategories(backendAcctId);
        window.dispatchEvent(new CustomEvent('categoryListUpdated'));
        setIsDeleteConfirmOpen(false);
        setCategoryToDelete(null);
        showSuccess('Category deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      setDeleteError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCategory = (updatedData) => {

    setEditMode(false);
    setOpenCategoryDialog(false);
    updateCategory(updatedData);
  };

  const updateCategory = async (updatedData) => {
    const token = localStorage.getItem('token');
    setIsLoading(true);
    try {
      const backendAcctId = acctId;
      if (!backendAcctId) {
        showError('No account ID available for category update');
        setIsLoading(false);
        return;
      }
      // Create a new FormData instance
      const formData = new FormData();
      Object.keys(updatedData).forEach(key => {
        if (key === '_id' && updatedData[key] === undefined) return;
        if (key === 'acctId') return; // Do NOT send any acctId from updatedData
        formData.append(key, updatedData[key]);
      });
      formData.append('acctId', backendAcctId); // Only send backend acctId

      const response = await fetch(`/api/ui/appointment/gCal/updateCategory`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData, // Send formData instead of JSON
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      fetchCategories(backendAcctId);
      window.dispatchEvent(new CustomEvent('categoryListUpdated'));
      showSuccess('Category updated successfully');
    } catch (error) {
      console.error('Error updating category:', error);
      showError('Failed to update category. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      <div className="bg-white shadow-md rounded-lg">
        <div className="p-4 flex justify-end items-center border-b">
          <button
            onClick={() => {
              setOpenCategoryDialog(true);
              setEditMode(false);
            }}
            className="rounded-md bg-black text-white py-2 px-4 flex items-center gap-2 hover:bg-gray-800 transition-colors"
            title="Add a new category"
          >
            <PlusIcon className="h-5 w-5" />
            Add
          </button>
        </div>
        <div className="overflow-x-auto relative">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category Name
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(isLoading || isDeleting) ? (
                [...Array(3)].map((_, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-2 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-pulse h-10 w-10 bg-gray-200 rounded mb-1" />
                      </div>
                    </td>
                    <td className="px-6 py-2 text-center">
                      <div className="h-3 w-24 bg-gray-200 rounded mb-1 animate-pulse mx-auto" />
                    </td>
                    <td className="px-6 py-2 text-center">
                      <div className="h-3 w-16 bg-gray-200 rounded animate-pulse mx-auto" />
                    </td>
                  </tr>
                ))
              ) : categories.length === 0 ? (
                <tr>
                  <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                    No categories found. Add your first category to get started.
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  category && (
                    <tr key={category._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center h-16 w-16 bg-gray-100 rounded overflow-hidden mx-auto">
                          {category.imageUrl ? (
                            <img
                              src={category.imageUrl}
                              alt={category.name || 'Category'}
                              className="h-full max-w-full object-contain"
                            />
                          ) : (
                            <div className="text-gray-400 text-xs">No Image</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-gray-900">{category.name || 'Unnamed Category'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => handleEditClick(category)}
                            className="text-black hover:text-gray-600 bg-gray-100 p-2 rounded-full transition-colors inline-flex"
                            title="Edit this category"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(category)}
                            className="text-red-600 hover:text-red-800 bg-gray-100 p-2 rounded-full transition-colors inline-flex"
                            title="Delete this category"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AddCategoryDialog
        isOpen={openCategoryDialog}
        onClose={() => setOpenCategoryDialog(false)}
        onSubmit={handleEditCategory}
        initialData={currentCategory}
        isEditing={editMode}
      />

      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {deleteError ? "Cannot Delete Category" : "Confirm Deletion"}
            </h3>

            {deleteError ? (
              <div className="mb-4">
                <p className="text-sm text-red-600 mb-2">{deleteError}</p>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete the category "{categoryToDelete?.name}"? This action cannot be undone.
              </p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                disabled={isDeleting}
              >
                {deleteError ? "Close" : "Cancel"}
              </button>

              {!deleteError && (
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center justify-center"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                      Deleting...
                    </>
                  ) : 'Delete'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoryPage;
